import { useState } from 'react'
import { cn } from '@/lib/cn'
import { initials } from '@/lib/format'

/** A usable photo address, or undefined for empty, blank or placeholder values ("null", "undefined"). */
export function photoUrl(src?: string | null) {
  const url = src?.trim()
  return url && url !== 'null' && url !== 'undefined' ? url : undefined
}

/**
 * Initials in a circle, with the photo laid over them once it has actually
 * loaded. A photo that fails (deleted file, unreachable host, bad value)
 * leaves the initials — never a blank circle.
 */
export function Avatar({
  name,
  src,
  size = 32,
  className,
  onLoadChange,
}: {
  name: string
  src?: string
  size?: number
  className?: string
  /** Reports whether the photo is showing, e.g. to offer «open photo» only then. */
  onLoadChange?: (loaded: boolean) => void
}) {
  const url = photoUrl(src)
  // Keyed by the address, so a new photo starts over and an old failure does not stick.
  const [state, setState] = useState<{ url: string; status: 'loaded' | 'failed' } | null>(null)
  const status = state && state.url === url ? state.status : 'loading'
  const report = (next: 'loaded' | 'failed') => {
    if (!url) return
    setState({ url, status: next })
    onLoadChange?.(next === 'loaded')
  }

  return (
    <span
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-b from-[var(--hover)] to-[var(--press)] font-medium text-fg-2 ring-1 ring-line',
        className,
      )}
      style={{ width: size, height: size, fontSize: Math.max(10, size * 0.36) }}
      aria-hidden
    >
      {initials(name)}
      {url && status !== 'failed' && (
        <img
          src={url}
          alt=""
          onLoad={() => report('loaded')}
          onError={() => report('failed')}
          className={cn('absolute inset-0 size-full object-cover transition-opacity duration-[var(--dur-base)]', status === 'loaded' ? 'opacity-100' : 'opacity-0')}
        />
      )}
    </span>
  )
}

/**
 * «Фото» cell: the avatar, and — only when the photo really loaded — a link
 * that opens it in a new tab. A missing or broken photo is just the initials.
 */
export function PhotoAvatar({ name, src, label, size = 30 }: { name: string; src?: string; label: string; size?: number }) {
  const [loaded, setLoaded] = useState(false)
  const url = photoUrl(src)
  const link = Boolean(url && loaded)
  // Always the same wrapper element, so the avatar is not remounted (and does not flash) when the link turns on.
  return (
    <a
      href={link ? url : undefined}
      target={link ? '_blank' : undefined}
      rel={link ? 'noopener noreferrer' : undefined}
      title={link ? label : undefined}
      aria-label={link ? label : undefined}
      onClick={link ? (event) => event.stopPropagation() : undefined}
      className={cn('inline-flex rounded-full outline-none', link && 'transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-accent')}
    >
      <Avatar name={name} src={url} size={size} onLoadChange={setLoaded} />
    </a>
  )
}
